import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SESSION_STORAGE_KEYS } from '../../constants';

// Mirror the PromptDialog interface from chat.component.ts
export interface PromptDialog {
  uuid: string;
  type: 'prompt' | 'response' | 'loading' | 'buttons' | 'list' | 'embeddedLink' | 'dashboards';
  value?: string;
  buttons?: string[];
  listItems?: string[];
  textBeforeButtons?: string;
  textAfterButtons?: string;
  dashboards?: { title: string; dashboardId: string }[];
}

@Injectable({
  providedIn: 'root'
})
export class ChatHistoryService {
  private chatHistory = new BehaviorSubject<PromptDialog[]>([]);
  public chatHistory$ = this.chatHistory.asObservable();

  constructor() {
    this.loadHistory();
  }

  /**
   * Save the current chat dialog to sessionStorage (tab-isolated)
   * @param dialogs Array of PromptDialog objects to save
   */
  saveHistory(dialogs: PromptDialog[]): void {
    // Filter out loading messages before saving
    const filteredDialogs = dialogs.filter(dialog => dialog.type !== 'loading');
    
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.CHAT_HISTORY, JSON.stringify(filteredDialogs));
      this.chatHistory.next(filteredDialogs);
    } catch (e) {
      console.error('Error saving chat history:', e);
    }
  }

  /**
   * Load chat history from sessionStorage
   */
  loadHistory(): void {
    try {
      const savedHistory = sessionStorage.getItem(SESSION_STORAGE_KEYS.CHAT_HISTORY);
      if (savedHistory) {
        const parsedHistory = JSON.parse(savedHistory) as PromptDialog[];
        this.chatHistory.next(parsedHistory);
      }
    } catch (e) {
      console.error('Error loading chat history:', e);
      this.chatHistory.next([]);
    }
  }

  /**
   * Clear chat history from sessionStorage
   */
  clearHistory(): void {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.CHAT_HISTORY);
    this.chatHistory.next([]);
  }

  /**
   * Get current chat history
   */
  getHistory(): PromptDialog[] {
    return this.chatHistory.value;
  }
}
